import { DOCUMENT } from '@angular/common';
import { DomPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, NgZone, OnDestroy, ViewEncapsulation, effect, inject, input, signal, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
import { plusIcon, arrowLeftIcon, commentIcon, xIcon } from '@progress/kendo-svg-icons';
import { CopilotChatService } from '../../services/custom-services/copilot-chat.service';
import { CopilotChatComponent } from './copilot-chat.component';

@Component({
  selector: 'app-copilot-chat-host',
  standalone: true,
  imports: [TranslateModule, KENDO_BUTTONS, KENDO_DIALOGS, CopilotChatComponent],
  template: `
    <div
      #surface
      class="optinex-chat-host"
    >
      @if (available() && chat.enabled()) {
      <button
        class="optinex-chat-launcher"
        kendoButton
        type="button"
        themeColor="primary"
        [svgIcon]="chatIcon"
        [title]="'Chat.Open' | translate"
        [attr.aria-label]="'Chat.Open' | translate"
        [attr.aria-expanded]="opened()"
        aria-controls="optinex-chat-window"
        (click)="open()">
      </button>
      @if (opened()) {
        <kendo-window
          id="optinex-chat-window"
          class="optinex-chat-window"
          themeColor="primary"
          [title]="'Chat.Title' | translate"
          [width]="width()"
          [height]="height()"
          [left]="left()"
          [top]="top()"
          [minWidth]="240"
          [minHeight]="260"
          [draggable]="!mobile()"
          [resizable]="!mobile()"
          autoFocusedElement="textarea"
          (leftChange)="left.set($event)"
          (topChange)="top.set($event)"
          (widthChange)="width.set($event)"
          (heightChange)="height.set($event)"
          (dragEnd)="constrain()"
          (resizeEnd)="constrain()"
          (close)="close()"
        >
          <kendo-window-titlebar>
            <span class="optinex-chat-title">{{ 'Chat.Title' | translate }}</span>
            <button
              kendoButton
              type="button"
              fillMode="flat"
              [svgIcon]="returnIcon"
              [title]="'Chat.ReturnToDialog' | translate"
              [attr.aria-label]="'Chat.ReturnToDialog' | translate"
              (click)="returnToDialog()">
            </button>
            <button
              kendoButton
              type="button"
              fillMode="flat"
              [svgIcon]="newIcon"
              [disabled]="chat.busy()"
              [title]="'Chat.NewConversation' | translate"
              [attr.aria-label]="'Chat.NewConversation' | translate"
              (click)="newConversation()">
            </button>
            <button
              kendoButton
              type="button"
              fillMode="flat"
              [svgIcon]="closeIcon"
              [title]="'Chat.Close' | translate"
              [attr.aria-label]="'Chat.Close' | translate"
              (click)="close()">
            </button>
          </kendo-window-titlebar>
          <app-copilot-chat></app-copilot-chat>
        </kendo-window>
        }
     }
    </div>
  `,
  styles: [
    `
      .optinex-chat-host,
      .optinex-chat-portal-outlet {
        display: contents;
      }
      .optinex-chat-launcher {
        position: fixed;
        right: 20px;
        bottom: 20px;
        width: 48px;
        height: 48px;
        z-index: var(--optinex-chat-layer, 100);
      }
      .optinex-chat-window {
        position: fixed !important;
        z-index: var(--optinex-chat-layer, 100) !important;
        max-width: 100vw;
        max-height: 100dvh;
      }
      .optinex-chat-window .k-window-content {
        padding: 0;
        min-height: 0;
        overflow: hidden;
      }
      .optinex-chat-window .k-window-titlebar {
        gap: 4px;
      }
      .optinex-chat-title {
        flex: 1;
        min-width: 0;
        font-size: 16px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .optinex-chat-window .chat-markdown p {
        margin: 0 0 8px;
      }
      .optinex-chat-window .chat-markdown pre,
      .optinex-chat-window .chat-markdown table {
        display: block;
        max-width: 100%;
        overflow-x: auto;
      }
      .optinex-chat-window .chat-markdown pre {
        background: var(--kendo-color-base-subtle, #f4f4f4);
        padding: 8px;
      }
      .optinex-chat-window .chat-markdown h1,
      .optinex-chat-window .chat-markdown h2,
      .optinex-chat-window .chat-markdown h3 {
        font-size: 16px;
      }
      .optinex-chat-window .chat-markdown a {
        color: var(--kendo-color-primary-on-surface, #006d77);
        overflow-wrap: anywhere;
      }
      @media (max-width: 600px) {
        .optinex-chat-launcher {
          right: 12px;
          bottom: 12px;
        }
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopilotChatHostComponent implements AfterViewInit, OnDestroy {
  readonly available = input.required<boolean>();
  readonly opened = signal(false);
  readonly width = signal(420);
  readonly height = signal(560);
  readonly left = signal(0);
  readonly top = signal(0);
  readonly mobile = signal(false);
  readonly chatIcon = commentIcon;
  readonly newIcon = plusIcon;
  readonly closeIcon = xIcon;
  readonly returnIcon = arrowLeftIcon;
  readonly chat = inject(CopilotChatService);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly surface = viewChild.required<ElementRef<HTMLElement>>('surface');
  private outlet: DomPortalOutlet | null = null;
  private target: HTMLElement | null = null;
  private observer: MutationObserver | null = null;
  private previousFocus: HTMLElement | null = null;
  private dialogFocus: HTMLElement | null = null;
  private frame: number | null = null;
  private readonly keydown = (event: KeyboardEvent) => this.onKeydown(event);
  private readonly resize = () => this.zone.run(() => this.constrain());
  private readonly focusin = (event: FocusEvent) => {
    const target = event.target;
    if (target instanceof HTMLElement && this.target?.contains(target) && !this.surface().nativeElement.contains(target)) {
      this.dialogFocus = target;
    }
  };

  constructor() {
    effect(() => {
      if (!this.available() || !this.chat.signedIn()) {
        this.opened.set(false);
        this.chat.reset();
      }
    });
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.scheduleRehost());
      this.observer.observe(this.document.body, { childList: true, subtree: true });
      this.document.addEventListener('keydown', this.keydown, true);
      this.document.addEventListener('focusin', this.focusin);
      this.document.defaultView?.addEventListener('resize', this.resize);
      this.document.defaultView?.visualViewport?.addEventListener('resize', this.resize);
    });
    this.rehost();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
    }
    this.outlet?.dispose();
    this.document.removeEventListener('keydown', this.keydown, true);
    this.document.removeEventListener('focusin', this.focusin);
    this.document.defaultView?.removeEventListener('resize', this.resize);
    this.document.defaultView?.visualViewport?.removeEventListener('resize', this.resize);
  }

  open(): void {
    if (!this.available() || !this.chat.enabled()) {
      return;
    }
    if (!this.opened()) {
      this.previousFocus = this.document.activeElement instanceof HTMLElement ? this.document.activeElement : null;
      this.constrain(true);
      this.opened.set(true);
    }
    void this.chat.connect();
    requestAnimationFrame(() => this.focusComposer());
  }

  close(): void {
    this.opened.set(false);
    const destination = this.previousFocus?.isConnected ? this.previousFocus : this.surface().nativeElement.querySelector<HTMLElement>('.optinex-chat-launcher');
    destination?.focus({ preventScroll: true });
  }

  returnToDialog(): void {
    const destination = this.dialogFocus?.isConnected ? this.dialogFocus : this.target?.querySelector<HTMLElement>('input, textarea, button, [tabindex="0"]');
    (destination ?? this.previousFocus)?.focus({ preventScroll: true });
  }

  newConversation(): void {
    void this.chat.newConversation();
    this.focusComposer();
  }

  constrain(initial = false): void {
    const viewport = this.document.defaultView?.visualViewport;
    const viewportWidth = viewport?.width ?? this.document.documentElement.clientWidth;
    const viewportHeight = viewport?.height ?? this.document.documentElement.clientHeight;
    const offsetLeft = viewport?.offsetLeft ?? 0;
    const offsetTop = viewport?.offsetTop ?? 0;
    const mobile = viewportWidth <= 600;
    this.mobile.set(mobile);
    this.width.set(mobile ? viewportWidth : Math.min(initial ? 420 : this.width(), viewportWidth - 16));
    this.height.set(mobile ? viewportHeight : Math.min(initial ? 560 : this.height(), viewportHeight - 16));
    this.left.set(mobile ? offsetLeft : Math.max(offsetLeft + 8, Math.min(initial ? viewportWidth - this.width() - 20 : this.left(), offsetLeft + viewportWidth - this.width() - 8)));
    this.top.set(mobile ? offsetTop : Math.max(offsetTop + 8, Math.min(initial ? viewportHeight - this.height() - 80 : this.top(), offsetTop + viewportHeight - this.height() - 8)));
  }

  private scheduleRehost(): void {
    if (this.frame === null) {
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.rehost();
      });
    }
  }

  private rehost(): void {
    const surface = this.surface().nativeElement;
    const dialogs = Array.from(this.document.querySelectorAll<HTMLElement>('.k-dialog-wrapper .k-dialog[aria-modal="true"]')).filter(dialog => dialog.isConnected && dialog.getClientRects().length > 0);
    const layer = (element: HTMLElement) => Number(this.document.defaultView?.getComputedStyle(element).zIndex) || 0;
    dialogs.sort((first, second) => layer(first.closest<HTMLElement>('.k-dialog-wrapper') ?? first) - layer(second.closest<HTMLElement>('.k-dialog-wrapper') ?? second));
    const target = dialogs[dialogs.length - 1] ?? null;
    const layers = Array.from(this.document.querySelectorAll<HTMLElement>('.k-dialog-wrapper, .k-window, .k-overlay')).filter(element => !surface.contains(element));
    const nextLayer = Math.max(100, ...layers.map(layer)) + 1;
    surface.style.setProperty('--optinex-chat-layer', String(nextLayer));
    if (this.target === target) {
      return;
    }
    const focused = surface.contains(this.document.activeElement) ? (this.document.activeElement as HTMLElement) : null;
    this.outlet?.dispose();
    this.outlet = null;
    this.target = target;
    this.dialogFocus = null;
    if (target) {
      const outletElement = this.document.createElement('div');
      outletElement.className = 'optinex-chat-portal-outlet';
      target.appendChild(outletElement);
      this.outlet = new DomPortalOutlet(outletElement);
      this.outlet.attach(new DomPortal(surface));
    }
    focused?.focus({ preventScroll: true });
  }

  private focusComposer(): void {
    this.surface().nativeElement.querySelector<HTMLElement>('textarea')?.focus({ preventScroll: true });
  }

  private onKeydown(event: KeyboardEvent): void {
    if (!this.available() || !this.chat.enabled()) {
      return;
    }
    const inChat = event.target instanceof Node && this.surface().nativeElement.contains(event.target);
    if (event.key === 'Escape' && this.opened() && inChat) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.zone.run(() => this.close());
    } else if (event.key === 'F6' && !event.ctrlKey && !event.altKey && !event.metaKey) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.zone.run(() => (inChat && this.opened() ? this.returnToDialog() : this.open()));
    }
  }
}
